/**
 * The single source of application configuration.
 *
 * One image is built once in CI and deployed to acceptance and production with
 * different settings, so configuration cannot be baked into the bundle. The app
 * reads it at *runtime* from `window.__APP_CONFIG__`, a small `env-config.js`
 * that the container entrypoint writes from real environment variables before
 * nginx starts (Chunk 6). For local `bun run dev` that file does not exist and
 * we fall back to Vite's build-time `import.meta.env`.
 *
 * This module is the only place in the app allowed to touch either mechanism —
 * an ESLint rule enforces it. Everything else imports `config` from here.
 */

/** The settings the app needs, in the shape the rest of the code consumes. */
export interface AppConfig {
  /** Base URL of the dashboard API, without a trailing slash. */
  apiUrl: string;
  /** Keycloak server base URL, without a trailing slash. */
  keycloakBaseUrl: string;
  /** Realm holding the dashboard users. */
  keycloakRealm: string;
  /** Public (PKCE) client id registered in that realm. */
  keycloakClientId: string;
  /** Where Keycloak sends the browser back after login. */
  redirectUri: string;
}

/**
 * The keys as they appear in `env-config.js` and in the container environment.
 * Deliberately the plain, unprefixed names an operator sets in Compose.
 */
export type RuntimeConfigKey =
  'API_URL' | 'KEYCLOAK_BASE_URL' | 'KEYCLOAK_REALM' | 'KEYCLOAK_CLIENT_ID' | 'REDIRECT_URI';

declare global {
  interface Window {
    /** Written by the container entrypoint; absent during local development. */
    __APP_CONFIG__?: Partial<Record<RuntimeConfigKey, string>>;
  }
}

/**
 * The build-time fallback, written out statically so Vite can substitute each
 * reference. A dynamic `import.meta.env[key]` lookup would not survive the
 * production build.
 */
const buildTimeEnv: Record<RuntimeConfigKey, string | undefined> = {
  API_URL: import.meta.env.VITE_API_URL,
  KEYCLOAK_BASE_URL: import.meta.env.VITE_KEYCLOAK_BASE_URL,
  KEYCLOAK_REALM: import.meta.env.VITE_KEYCLOAK_REALM,
  KEYCLOAK_CLIENT_ID: import.meta.env.VITE_KEYCLOAK_CLIENT_ID,
  REDIRECT_URI: import.meta.env.VITE_REDIRECT_URI,
};

/**
 * A value counts as present only if it is a non-empty string. The entrypoint
 * emits a key for every variable it knows about, so an unset one arrives as an
 * empty string rather than as a missing key; an unsubstituted `${VAR}` means
 * the same thing.
 */
function clean(value: string | undefined): string | undefined {
  if (typeof value !== 'string') return undefined;
  const trimmed = value.trim();
  if (trimmed === '' || /^\$\{.*\}$/.test(trimmed)) return undefined;
  return trimmed;
}

/** URLs are stored without a trailing slash so callers can always append one. */
function stripTrailingSlash(value: string): string {
  return value.replace(/\/+$/, '');
}

/** Runtime global first, build-time env second, empty string if neither. */
export function readRuntimeValue(key: RuntimeConfigKey): string {
  const fromWindow =
    typeof window === 'undefined' ? undefined : clean(window.__APP_CONFIG__?.[key]);
  return fromWindow ?? clean(buildTimeEnv[key]) ?? '';
}

export function loadConfig(): AppConfig {
  return {
    apiUrl: stripTrailingSlash(readRuntimeValue('API_URL')),
    keycloakBaseUrl: stripTrailingSlash(readRuntimeValue('KEYCLOAK_BASE_URL')),
    keycloakRealm: readRuntimeValue('KEYCLOAK_REALM'),
    keycloakClientId: readRuntimeValue('KEYCLOAK_CLIENT_ID'),
    redirectUri: readRuntimeValue('REDIRECT_URI'),
  };
}

/**
 * Which settings are still missing. Nothing in the scaffold needs them yet, so
 * an incomplete configuration surfaces as a visible warning rather than a crash;
 * Chunk 1 onwards can treat this as a hard failure once auth depends on it.
 */
export function missingConfigKeys(candidate: AppConfig = config): RuntimeConfigKey[] {
  const byKey: Record<RuntimeConfigKey, string> = {
    API_URL: candidate.apiUrl,
    KEYCLOAK_BASE_URL: candidate.keycloakBaseUrl,
    KEYCLOAK_REALM: candidate.keycloakRealm,
    KEYCLOAK_CLIENT_ID: candidate.keycloakClientId,
    REDIRECT_URI: candidate.redirectUri,
  };
  return (Object.keys(byKey) as RuntimeConfigKey[]).filter((key) => byKey[key] === '');
}

/**
 * Read once at module load. `env-config.js` is a plain script in `<head>`, so
 * the global is already in place by the time the module graph evaluates.
 */
export const config: AppConfig = loadConfig();
